import React, { useMemo } from "react";
import { ListItem } from "@mui/material";
import { HabitClass, HabitClassHabitEntriesItem } from "../../generated/model";
import HabitCalendarHeader from "../HabitCalendarHeader";
import HabitCalendarWeekView from "../HabitCalendarWeekView";
import ContributionHeatmap, {
  formatContributionData,
  type ViewPeriod,
} from "../ContributionHeatmap";

// Helper type for the onClick value from CalendarHeatmap, acknowledging mixed date types
interface HabitCalendarProps {
  habit: HabitClass;
  habitEntries: HabitClassHabitEntriesItem[];
  onDayClick?: (date: string, habitId: string) => void;
  viewPeriod: ViewPeriod;
}

const HabitCalendar: React.FC<HabitCalendarProps> = ({
  habit,
  habitEntries,
  onDayClick,
  viewPeriod,
}) => {
  // Memoize the contribution data to avoid recalculating on every render
  const contributionData = useMemo(() => {
    return formatContributionData(habit, habitEntries, viewPeriod);
  }, [habit, habitEntries, viewPeriod]);

  const handleCellClick = (date: string) => {
    if (onDayClick) {
      onDayClick(date, habit.id);
    }
  };

  // If view period is "week", use the specialized week view
  if (viewPeriod === "week") {
    return (
      <ListItem
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          padding: 0,
        }}
      >
        <HabitCalendarWeekView
          habit={habit}
          habitEntries={habitEntries}
          onDayClick={onDayClick || (() => {})}
        />
      </ListItem>
    );
  }

  return (
    <ListItem
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        padding: 0,
      }}
    >
      <HabitCalendarHeader habit={habit} />
      <ContributionHeatmap
        data={contributionData}
        viewPeriod={viewPeriod}
        onCellClick={handleCellClick}
        cellSize={20}
        gap={2}
      />
    </ListItem>
  );
};

export default HabitCalendar;
